import { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthProvider';
import EmploymentJobForm from './EmploymentJobForm';
import {
  addFreelanceWorkerToCompany,
  deleteEmploymentJob,
  fetchCompanyEmploymentJobs,
  fetchEmploymentJobCandidates,
  removeFreelanceWorkerFromCompany,
  saveEmploymentJob,
} from '../services/EmploymentJobService';
import './FlexibleEmployment.scss';

function getJobId(job) {
  return job?.id || job?.jobId || '';
}

function formatDate(dateValue) {
  if (!dateValue) return 'Not set';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return dateValue;
  return date.toLocaleDateString();
}

function normalizedSkills(job) {
  if (Array.isArray(job?.skillsRequired)) return job.skillsRequired.filter(Boolean);
  if (typeof job?.skillsRequired === 'string') {
    return job.skillsRequired.split(',').map((item) => item.trim()).filter(Boolean);
  }
  return [];
}

function candidateUserPayload(candidate, companyId) {
  const userId = candidate?.userId ||  '';
  return {
    ...candidate,
    id: candidate.id,
    userId,
    companyId,
  };
}

export default function EmployerEmploymentJobsPage() {
  const authContext = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [candidateMap, setCandidateMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [editingJob, setEditingJob] = useState(null);
  const [savingJob, setSavingJob] = useState(false);
  const [candidateDialogJob, setCandidateDialogJob] = useState(null);
  const [addingCandidateId, setAddingCandidateId] = useState('');

  const companyId = authContext.additionalUserInfo?.companyId;

  const loadJobs = useCallback(async () => {
    if (!authContext.token || !companyId) {
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const companyJobs = await fetchCompanyEmploymentJobs(authContext.token, companyId);
      setJobs(companyJobs);

      const entries = await Promise.all(
        companyJobs.map(async (job) => {
          const id = getJobId(job);
          const candidates = id ? await fetchEmploymentJobCandidates(authContext.token, id) : [];
          return [id, candidates];
        })
      );

      setCandidateMap(Object.fromEntries(entries));
    } catch (error) {
      console.error('Failed to load employment jobs:', error);
      setMessage({ type: 'error', text: 'Unable to load your flexible employment jobs right now.' });
      setJobs([]);
      setCandidateMap({});
    } finally {
      setLoading(false);
    }
  }, [authContext.token, companyId]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  const selectedCandidates = useMemo(() => {
    const jobId = getJobId(candidateDialogJob);
    return candidateMap[jobId] || [];
  }, [candidateDialogJob, candidateMap]);

  const handleDelete = async (jobId) => {
    if (!jobId || !window.confirm('Delete this employment job?')) {
      return;
    }

    try {
      await deleteEmploymentJob(authContext.token, jobId);
      setMessage({ type: 'success', text: 'Job deleted.' });
      await loadJobs();
    } catch (error) {
      console.error('Failed to delete employment job:', error);
      setMessage({ type: 'error', text: 'Unable to delete this job right now.' });
    }
  };

  const handleCloseJob = async (job) => {
    try {
      await saveEmploymentJob(authContext.token, {
        ...job,
        id: getJobId(job),
        jobStatus: 'closed',
      });
      setMessage({ type: 'success', text: 'Job closed.' });
      await loadJobs();
    } catch (error) {
      console.error('Failed to close employment job:', error);
      setMessage({ type: 'error', text: 'Unable to close this job right now.' });
    }
  };

  const handleEditSubmit = async (job) => {
    setSavingJob(true);
    setMessage({ type: '', text: '' });

    try {
      await saveEmploymentJob(authContext.token, {
        ...editingJob,
        ...job,
        id: getJobId(editingJob),
        companyId: editingJob?.companyId || companyId,
        creatorId: editingJob?.creatorId || authContext.additionalUserInfo?.id || authContext.additionalUserInfo?.userId || '',
      });
      setEditingJob(null);
      setMessage({ type: 'success', text: 'Job updated.' });
      await loadJobs();
    } catch (error) {
      console.error('Failed to update employment job:', error);
      setMessage({ type: 'error', text: 'Unable to update this job right now.' });
    } finally {
      setSavingJob(false);
    }
  };

  const handleAddToWorkforce = async (candidate) => {
    const candidateId = candidate?.id || candidate?.userId || candidate?.employeeId || '';
    if (!candidateId) {
      setMessage({ type: 'error', text: 'Candidate is missing a user id.' });
      return;
    }

    setAddingCandidateId(candidateId);
    setMessage({ type: '', text: '' });

    try {
      await addFreelanceWorkerToCompany(authContext.token, candidateUserPayload(candidate, companyId));
      setMessage({ type: 'success', text: 'Candidate added to your workforce.' });
      await loadJobs();
    } catch (error) {
      console.error('Failed to add candidate to workforce:', error);
      setMessage({ type: 'error', text: 'Unable to add this candidate to your workforce right now.' });
    } finally {
      setAddingCandidateId('');
    }
  };

  const handleRemoveFromWorkforce = async (candidate) => {
    const candidateId = candidate?.id || candidate?.userId || candidate?.employeeId || '';
    if (!candidateId) {
      setMessage({ type: 'error', text: 'Candidate is missing a user id.' });
      return;
    }

    setAddingCandidateId(candidateId);
    setMessage({ type: '', text: '' });

    try {
      await removeFreelanceWorkerFromCompany(authContext.token, candidateUserPayload(candidate, companyId));
      setMessage({ type: 'success', text: 'Candidate removed from your workforce.' });
      await loadJobs();
    } catch (error) {
      console.error('Failed to remove candidate from workforce:', error);
      setMessage({ type: 'error', text: 'Unable to remove this candidate from your workforce right now.' });
    } finally {
      setAddingCandidateId('');
    }
  };

  return (
    <div className="flex-employment-page">
      <div className="flex-employment-shell">
        <div className="flex-employment-hero">
          <div>
            <h1>Flexible employment</h1>
            <p>Manage temporary staffing ads, review candidates, and move suitable freelance workers into your rostered workforce.</p>
          </div>
          <div className="flex-employment-actions">
            <button className="flex-employment-button" type="button" onClick={() => navigate('/flexible-employment/create-job')}>
              Create job
            </button>
            <button className="flex-employment-button-secondary" type="button" onClick={loadJobs}>
              Refresh
            </button>
          </div>
        </div>

        {message.text && message.type === 'error' && <div className="flex-employment-error">{message.text}</div>}
        {message.text && message.type === 'success' && <div className="flex-employment-success">{message.text}</div>}

        {!companyId && (
          <div className="flex-employment-error">
            Your company id is missing. Complete your company profile before advertising temporary roles.
          </div>
        )}

        {loading && <div className="flex-employment-loading">Loading jobs...</div>}

        {!loading && jobs.length === 0 && (
          <div className="flex-employment-empty">No flexible employment jobs have been advertised yet.</div>
        )}

        {!loading && jobs.length > 0 && (
          <div className="flex-employment-grid">
            {jobs.map((job) => {
              const jobId = getJobId(job);
              const candidates = candidateMap[jobId] || [];
              const status = (job?.jobStatus || 'open').toLowerCase();

              return (
                <div className="flex-employment-card" key={jobId || job.jobTitle}>
                  <div>
                    <span className={`flex-employment-status ${status}`}>{status}</span>
                  </div>
                  <div>
                    <h3>{job.jobTitle || 'Untitled temporary role'}</h3>
                    <p>{job.jobDescription || 'No description provided.'}</p>
                  </div>

                  <div className="flex-employment-meta">
                    <div>
                      <strong>Dates</strong>
                      <span>{formatDate(job.fromDate)} to {formatDate(job.toDate)}</span>
                    </div>
                    <div>
                      <strong>Closing date</strong>
                      <span>{formatDate(job.adClosingDate)}</span>
                    </div>
                    <div>
                      <strong>Location</strong>
                      <span>{job.jobLocation || job.cityName || 'Not set'}</span>
                    </div>
                    <div>
                      <strong>Headcount</strong>
                      <span>{job.numberOfPeopleRequired || 0}</span>
                    </div>
                    <div>
                      <strong>Type</strong>
                      <span>{job.employmentType || 'Not set'}</span>
                    </div>
                    <div>
                      <strong>Hourly rate</strong>
                      <span>{job.hourlyRate ? `$${job.hourlyRate}` : 'Not set'}</span>
                    </div>
                  </div>

                  <div className="flex-employment-skills">
                    {normalizedSkills(job).length > 0 ? normalizedSkills(job).map((skill) => (
                      <span className="flex-employment-skill" key={skill}>{skill}</span>
                    )) : <span>No skills listed</span>}
                  </div>

                  <div className="flex-employment-banner">
                    {candidates.length} candidate{candidates.length === 1 ? '' : 's'} applied
                  </div>

                  <div className="flex-employment-card-actions">
                    <button className="flex-employment-button" type="button" onClick={() => setEditingJob(job)}>
                      Edit
                    </button>
                    {status !== 'closed' && (
                      <button className="flex-employment-button-secondary" type="button" onClick={() => handleCloseJob(job)}>
                        Close
                      </button>
                    )}
                    <button className="flex-employment-button-danger" type="button" onClick={() => handleDelete(jobId)}>
                      Delete
                    </button>
                    {candidates.length > 0 && (
                      <button className="flex-employment-button-secondary" type="button" onClick={() => setCandidateDialogJob(job)}>
                        View candidates
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <Dialog open={Boolean(editingJob)} onClose={() => setEditingJob(null)} maxWidth="md" fullWidth>
          <DialogTitle>Edit flexible employment job</DialogTitle>
          <DialogContent>
            {editingJob && (
              <EmploymentJobForm
                initialJob={editingJob}
                submitLabel="Save changes"
                submitting={savingJob}
                onSubmit={handleEditSubmit}
                onCancel={() => setEditingJob(null)}
              />
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={Boolean(candidateDialogJob)} onClose={() => setCandidateDialogJob(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Job candidates</DialogTitle>
          <DialogContent>
            <div className="flex-employment-candidate-list">
              {selectedCandidates.length === 0 && (
                <div className="flex-employment-empty">No candidates found for this job.</div>
              )}

              {selectedCandidates.map((candidate) => {
                const candidateId = candidate?.id || candidate?.userId || candidate?.employeeId || candidate?.emailAddress;
                const fullName = `${candidate?.firstName || ''} ${candidate?.lastName || ''}`.trim() || 'Unnamed candidate';
                const isInWorkforce = (candidate?.addedToWorkforce || '').toUpperCase() === 'Y';

                return (
                  <div className="flex-employment-candidate" key={candidateId}>
                    <h4>{fullName}</h4>
                    <p>Email: {candidate?.emailAddress || candidate?.email || 'Not provided'}</p>
                    <p>Phone: {candidate?.phoneNumber || candidate?.phone || 'Not provided'}</p>
                    <div className="flex-employment-card-actions">
                      <button
                        className={isInWorkforce ? 'flex-employment-button-danger' : 'flex-employment-button'}
                        type="button"
                        disabled={addingCandidateId === candidateId}
                        onClick={() => {
                          if (isInWorkforce) {
                            handleRemoveFromWorkforce(candidate);
                            return;
                          }

                          handleAddToWorkforce(candidate);
                        }}
                      >
                        {addingCandidateId === candidateId
                          ? (isInWorkforce ? 'Removing...' : 'Adding...')
                          : (isInWorkforce ? 'Remove from workforce' : 'Add to my workforce')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}